import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Download, Search, ChevronRight, ChevronLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { translateSubType } from '@/utils/transactionTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const ClientReport = ({ isRTL, headers, onClose, showPrices = false }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const captureRef = useRef(null);

  const months = isRTL ? MONTHS_AR : MONTHS_EN;
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/clients`, { headers });
        setClients(res.data || []);
      } catch { /* ignore */ }
    };
    fetch();
  }, []);

  const selectClient = async (client) => {
    setSelectedClient(client);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/plan-groups?client_id=${client.id}`, { headers });
      setGroups(res.data || []);
    } catch { setGroups([]); }
    setLoading(false);
  };

  const filteredGroups = groups.filter(g => {
    const d = new Date(g.created_at);
    return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  });

  const prevMonth = () => {
    if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1); }
    else setSelectedMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1); }
    else setSelectedMonth(m => m + 1);
  };

  const captureImage = async () => {
    if (!captureRef.current) return;
    try {
      const canvas = await html2canvas(captureRef.current, { scale: 3, backgroundColor: '#ffffff', useCORS: true, logging: false });
      const link = document.createElement('a');
      link.download = `report-${selectedClient?.name}-${months[selectedMonth]}-${selectedYear}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(isRTL ? 'تم تحميل التقرير' : 'Report downloaded');
    } catch (e) { console.error(e); toast.error('Error'); }
  };

  const filtered = clients.filter(c => !searchText.trim() || c.name?.toLowerCase().includes(searchText.trim().toLowerCase()));

  // Client selection
  if (!selectedClient) {
    return (
      <div className="space-y-3 max-w-xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900" data-testid="tracking-report-title">
            {isRTL ? 'نظام المتابعة' : 'Tracking System'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <Card className="p-4 space-y-2">
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
            <Input value={searchText} onChange={e => setSearchText(e.target.value)} autoFocus
              placeholder={isRTL ? 'ابحث عن عميل...' : 'Search client...'}
              className={`h-9 text-sm ${isRTL ? 'pr-9' : 'pl-9'}`} data-testid="client-search" />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-0.5">
            {filtered.map(c => (
              <button key={c.id} onClick={() => selectClient(c)} data-testid={`client-${c.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="w-7 h-7 rounded-full bg-[#D4AF37] text-[#0F172A] text-xs font-bold flex items-center justify-center shrink-0">{c.name?.charAt(0)}</div>
                <span className="text-sm text-slate-700 font-medium">{c.name}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  const allPlans = filteredGroups.flatMap(g => g.plans || []);
  const allSteps = allPlans.flatMap(p => p.steps || []);
  const completedSteps = allSteps.filter(s => s.status === 'completed').length;
  const executingSteps = allSteps.filter(s => s.status === 'executing').length;
  const plannedSteps = allSteps.filter(s => s.status === 'planned').length;

  return (
    <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-900">
          {isRTL ? 'نظام المتابعة' : 'Tracking System'}: {selectedClient.name}
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { setSelectedClient(null); setGroups([]); }} className="text-xs">
            {isRTL ? 'تغيير' : 'Change'}
          </Button>
          <Button size="sm" onClick={captureImage} className="gap-1.5 text-xs bg-[#0F172A] hover:bg-[#1e293b]" data-testid="download-report-btn">
            <Download className="w-3.5 h-3.5" /> {isRTL ? 'تحميل' : 'Download'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex items-center gap-2 justify-center">
        <Button variant="ghost" size="sm" onClick={prevMonth}><PrevIcon className="w-4 h-4" /></Button>
        <span className="text-sm font-bold text-slate-700 min-w-[160px] text-center">{months[selectedMonth]} {selectedYear}</span>
        <Button variant="ghost" size="sm" onClick={nextMonth}><NextIcon className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin mx-auto" /></Card>
      ) : (
        <div ref={captureRef} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }} dir={isRTL ? 'rtl' : 'ltr'}>
          <ReportBody isRTL={isRTL} showPrices={showPrices}
            clientName={selectedClient.name} groups={filteredGroups} allSteps={allSteps}
            completedSteps={completedSteps} executingSteps={executingSteps} plannedSteps={plannedSteps}
            allPlans={allPlans} monthName={months[selectedMonth]} year={selectedYear} />
        </div>
      )}
    </div>
  );
};

const ReportBody = ({ isRTL, showPrices, clientName, groups, allSteps, completedSteps, executingSteps, plannedSteps, allPlans, monthName, year }) => {
  const totalAmount = groups.reduce((s, g) => s + (g.total_amount || 0), 0);
  const pct = allSteps.length > 0 ? Math.round((completedSteps / allSteps.length) * 100) : 0;
  const dir = isRTL ? 'rtl' : 'ltr';
  const alignEnd = isRTL ? 'left' : 'right';

  const t = {
    trackingSystem: isRTL ? 'نظام المتابعة' : 'Tracking System',
    monthlyReport: isRTL ? 'تقرير شهري — العميل' : 'Monthly Report — Client',
    completed: isRTL ? 'مكتمل' : 'Completed',
    inProgress: isRTL ? 'جاري' : 'In Progress',
    notStarted: isRTL ? 'لم يبدأ' : 'Not Started',
    worker: isRTL ? 'عامل' : 'worker(s)',
    step: isRTL ? 'خطوة' : 'step(s)',
    group: isRTL ? 'مجموعة' : 'group(s)',
    noPlans: isRTL ? 'لا توجد خطط في هذا الشهر' : 'No plans in this month',
    total: isRTL ? 'إجمالي' : 'Total',
    groupLabel: isRTL ? 'مجموعة' : 'Group',
  };

  return (
    <div style={{ backgroundColor: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif', direction: dir, minWidth: '600px' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#0F172A', padding: '24px 32px 18px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody><tr>
          <td style={{ verticalAlign: 'top' }}>
            <p style={{ fontSize: '24px', fontWeight: '900', color: '#D4AF37', margin: 0, letterSpacing: '1px' }}>AZIZI.CO</p>
            <div style={{ width: '40px', height: '3px', backgroundColor: '#D4AF37', marginTop: '6px' }} />
          </td>
          <td style={{ verticalAlign: 'top', textAlign: alignEnd }}>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: 0, fontWeight: '600' }}>{monthName} {year}</p>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', margin: '2px 0 0' }}>{t.trackingSystem}</p>
          </td>
        </tr></tbody></table>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '14px' }}><tbody><tr>
          <td style={{ verticalAlign: 'bottom' }}>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>{t.monthlyReport}</p>
            <p style={{ fontSize: '20px', fontWeight: '800', color: '#fff', margin: '2px 0 0' }}>{clientName}</p>
          </td>
          <td style={{ verticalAlign: 'bottom', textAlign: alignEnd }}>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>{allPlans.length} {t.worker} | {allSteps.length} {t.step}</p>
          </td>
        </tr></tbody></table>
      </div>

      {/* Stats */}
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}><tbody><tr>
        <td style={{ padding: '10px 24px', textAlign: 'center' }}>
          <span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#059669', verticalAlign: 'middle', margin: '0 4px' }} />
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#059669', verticalAlign: 'middle' }}>{completedSteps} {t.completed}</span>
        </td>
        <td style={{ padding: '10px 0', textAlign: 'center' }}>
          <span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#d97706', verticalAlign: 'middle', margin: '0 4px' }} />
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#d97706', verticalAlign: 'middle' }}>{executingSteps} {t.inProgress}</span>
        </td>
        <td style={{ padding: '10px 0', textAlign: 'center' }}>
          <span style={{ display: 'inline-block', width: '8px', height: '8px', backgroundColor: '#cbd5e1', verticalAlign: 'middle', margin: '0 4px' }} />
          <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', verticalAlign: 'middle' }}>{plannedSteps} {t.notStarted}</span>
        </td>
        <td style={{ padding: '10px 24px', textAlign: 'center' }}>
          <span style={{ fontSize: '18px', fontWeight: '900', color: '#0F172A' }}>{pct}%</span>
        </td>
      </tr></tbody></table>

      {/* Progress bar */}
      <div style={{ padding: '0 32px 10px', backgroundColor: '#f8fafc' }}>
        <div style={{ width: '100%', height: '6px', backgroundColor: '#e2e8f0', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct === 100 ? '#059669' : '#D4AF37' }} />
        </div>
      </div>

      {/* Groups */}
      {groups.length === 0 ? (
        <div style={{ padding: '40px 32px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#94a3b8' }}>{t.noPlans}</p>
        </div>
      ) : (
        <div style={{ padding: '12px 24px' }}>
          {groups.map((group, gIdx) => {
            const groupPlans = group.plans || [];
            const groupSteps = groupPlans.flatMap(p => p.steps || []);
            const groupDone = groupSteps.filter(s => s.status === 'completed').length;
            const groupPct = groupSteps.length > 0 ? Math.round((groupDone / groupSteps.length) * 100) : 0;

            return (
              <div key={gIdx} style={{ marginBottom: gIdx < groups.length - 1 ? '14px' : '0' }}>
                {/* Group header */}
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1e293b', marginBottom: '1px' }}><tbody><tr>
                  <td style={{ padding: '8px 14px', verticalAlign: 'middle' }}>
                    <span style={{ fontSize: '14px', fontWeight: '900', color: '#D4AF37', verticalAlign: 'middle' }}>{gIdx + 1}.</span>
                    <span style={{ fontSize: '13px', fontWeight: '800', color: '#fff', verticalAlign: 'middle', unicodeBidi: 'isolate', margin: '0 6px' }}>
                      {group.notes?.trim() || `${t.groupLabel} ${gIdx + 1}`}
                    </span>
                  </td>
                  <td style={{ padding: '8px 14px', textAlign: alignEnd, verticalAlign: 'middle' }}>
                    {showPrices && (
                      <span style={{ fontSize: '12px', fontWeight: '900', color: '#D4AF37', direction: 'ltr', unicodeBidi: 'isolate' }}>
                        {(group.total_amount || 0).toLocaleString()} AED
                      </span>
                    )}
                    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', margin: '0 8px' }}>
                      {groupPct}% {t.completed}
                    </span>
                  </td>
                </tr></tbody></table>

                {/* Plans table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0' }}>
                  <tbody>
                    {groupPlans.map((plan, pIdx) => {
                      const name = plan.employee_name || plan.company_name || '';
                      const steps = plan.steps || [];
                      const done = steps.filter(s => s.status === 'completed').length;

                      return (
                        <tr key={pIdx} style={{ borderBottom: pIdx < groupPlans.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: pIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '10px 14px', verticalAlign: 'middle', backgroundColor: '#0F172A', width: '1%', maxWidth: '180px' }}>
                            <p style={{ fontSize: '12px', fontWeight: '800', color: '#fff', margin: 0, whiteSpace: 'nowrap' }}>{name}</p>
                            <p style={{ fontSize: '9px', fontWeight: '600', color: '#D4AF37', margin: '2px 0 0' }}>{done}/{steps.length}</p>
                            {plan.notes?.trim() && (
                              <p style={{ fontSize: '8px', fontWeight: '500', color: '#94a3b8', margin: '4px 0 0', lineHeight: '1.3', whiteSpace: 'normal', unicodeBidi: 'isolate', direction: 'rtl' }}>
                                {plan.notes.trim()}
                              </p>
                            )}
                          </td>
                          {steps.map((step, sIdx) => {
                            const bgColor = step.status === 'completed' ? '#ecfdf5' : step.status === 'executing' ? '#fffbeb' : '#f8fafc';
                            const borderColor = step.status === 'completed' ? '#a7f3d0' : step.status === 'executing' ? '#fde68a' : '#e2e8f0';
                            const textColor = step.status === 'completed' ? '#047857' : step.status === 'executing' ? '#b45309' : '#64748b';
                            const icon = step.status === 'completed' ? '✓' : step.status === 'executing' ? '●' : '—';
                            const iconColor = step.status === 'completed' ? '#059669' : step.status === 'executing' ? '#d97706' : '#94a3b8';

                            return (
                              <td key={sIdx} style={{
                                padding: '6px 8px', verticalAlign: 'middle', textAlign: 'center',
                                backgroundColor: bgColor, borderRight: `1px solid ${borderColor}`, minWidth: '70px',
                              }}>
                                <p style={{ fontSize: '14px', fontWeight: '900', color: iconColor, margin: 0, lineHeight: '1' }}>{icon}</p>
                                <p style={{
                                  fontSize: '9px', fontWeight: '600', color: textColor, margin: '3px 0 0', lineHeight: '1.2',
                                  textDecoration: step.status === 'completed' ? 'line-through' : 'none',
                                }}>{isRTL ? (step.label_ar || step.label_en) : (step.label_en || translateSubType(step.label_ar, false))}</p>
                                {showPrices && (
                                  <p style={{ fontSize: '9px', fontWeight: '800', color: '#1e293b', margin: '2px 0 0', lineHeight: '1', direction: 'ltr' }}>
                                    {(step.price || 0).toLocaleString()}
                                  </p>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#0F172A', borderTop: '3px solid #D4AF37' }}><tbody><tr>
        <td style={{ padding: '12px 32px', verticalAlign: 'middle' }}>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: '600' }}>{allPlans.length} {t.worker} | {groups.length} {t.group}</span>
        </td>
        <td style={{ padding: '12px 32px', textAlign: alignEnd, verticalAlign: 'middle' }}>
          {showPrices && (
            <>
              <span style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.5)', margin: '0 8px' }}>{t.total}</span>
              <span style={{ fontSize: '18px', fontWeight: '900', color: '#D4AF37', direction: 'ltr' }}>{totalAmount.toLocaleString()} AED</span>
            </>
          )}
        </td>
      </tr></tbody></table>
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#f8fafc' }}><tbody><tr>
        <td style={{ padding: '6px 32px' }}><span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '700' }}>AZIZI.CO</span></td>
        <td style={{ padding: '6px 32px', textAlign: alignEnd }}><span style={{ fontSize: '10px', color: '#94a3b8' }}>{monthName} {year}</span></td>
      </tr></tbody></table>
    </div>
  );
};

export default ClientReport;
